/* eslint-disable react/prop-types */
import React, { useContext, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  ExtraSelectorContainer,
  TitleContainer,
  TitleOption,
  ExtrasTitle,
  ExtrasHighlight,
  ListContainer,
  StyledIcon,
} from '../styled';
import Icon from '../../../../../../components/Icon';
import ExtraSelectItem from './ExtraSelectItem';
import { IconButton } from '../../styled';
import ExtraGroupEditModal from './ExtraGroupEditModal';
import AddExtraModal from '../AddExtraModal';
import {
  addExtraGroup as addExtraGroupMutation,
  addExtraToGroup as addExtraToGroupMutation,
  ingestPlaceholderExtra as ingestPlaceholderExtraMutation,
  removeExtraGroupFromProduct as removeExtraGroupFromProductMutation,
} from '../../../../../../graphql/mutations';
import {
  brandCategoryProductsByStore as brandCategoryProductsByStoreQuery,
  productCategories as productCategoriesQuery,
} from '../../../../../../graphql/queries';
import { CopyPasteButtonContainer, CreateExtraBtn, Spacer } from './styled';
import Checkbox from '../../../../../../components/Checkbox';
import Button from '../../../../../../components/Button';
import { CopyPasteContext } from '../../../../../../providers/CopyPasteContextProvider';
import i18n from '@/utils/i18n';

function ExtraSelector({
  productId,
  extraGroups,
  allExtrasForBrand,
  selectedExtras,
  onChange,
  storeId,
  brandId,
}) {
  const [isAddingPlaceholderExtra, setIsAddingPlaceholderExtra] =
    useState(false);
  const [ingestPlaceholderExtra] = useMutation(ingestPlaceholderExtraMutation);
  const [addExtraToGroup] = useMutation(addExtraToGroupMutation);
  const [addExtraGroup, { loading: addingExtraGroup }] = useMutation(
    addExtraGroupMutation,
  );

  const copyPasteContext = useContext(CopyPasteContext);
  const [selectedExtraGroups, setSelectedExtraGroups] = useState({});
  const [hasCopied, setHasCopied] = useState(false);

  const [editingGroup, setEditingGroup] = useState();
  const [addExtraModalOpenFor, setAddExtraModalOpenFor] = useState('');
  const [removeExtraGroupFromProduct] = useMutation(
    removeExtraGroupFromProductMutation,
  );

  const defaultOpenGroup = extraGroups
    ? extraGroups.reduce(
        (acc, group) =>
          !!group.is_open || !!group.minimum_extras
            ? { ...acc, [group.extra_group_id]: true }
            : acc,
        {},
      )
    : [];
  const [openGroup, setOpenGroup] = useState(defaultOpenGroup);

  function hasReachedMin(group) {
    if (group.minimum_extras) {
      return (
        selectedExtras.filter((ex) => ex.group_id === group.extra_group_id)
          .length >= group.minimum_extras
      );
    }
    return false;
  }

  function hasReachedMax(group) {
    if (group.maximum_extras) {
      return (
        selectedExtras.filter((ex) => ex.group_id === group.extra_group_id)
          .length +
          1 >
        group.maximum_extras
      );
    }
    return false;
  }

  function onExtraChange(extra, group) {
    const extraIndex = selectedExtras.findIndex(
      (ex) => ex.extra_id === extra.extra_id,
    );
    const isSelected = extraIndex >= 0;

    // selected extras in other group
    const selectedOtherGroupExtras = selectedExtras.filter(
      (ex) =>
        ex.extra_id !== extra.extra_id &&
        group.items.find((i) => i.extra_id === ex.extra_id),
    );

    // true if the extra is the only item selected in its group
    const isExtraOnlySelectedInGroup =
      isSelected && !selectedOtherGroupExtras.length;
    // prevent deselecting item if it's the only selected extra in a group
    if (group.is_required && isExtraOnlySelectedInGroup) return false;

    const extras = selectedExtras || [];

    if (isSelected) {
      extras.splice(extraIndex, 1);
    } else {
      // prevent selecting more extras than maximum number of extras
      const exceedingMax = hasReachedMax(group);
      if (!exceedingMax) extras.push(extra);
    }

    const newExtras = extras.map((extraOpt) => ({
      ...extraOpt,
      is_selected: true,
    }));
    return onChange(newExtras);
  }

  function openAddExtraModal(e, group) {
    e.stopPropagation();
    setAddExtraModalOpenFor(group.extra_group_id);
  }

  function openEditModal(e, group) {
    e.stopPropagation();
    setEditingGroup(group);
  }

  function closeEditModal() {
    setEditingGroup(null);
  }

  function closeAddExtraModal() {
    setAddExtraModalOpenFor('');
  }

  function removeExtraGroup(e, group) {
    e.stopPropagation();
    const removable = window.confirm(
      i18n.t('Menu:removeExtraGroupWarning', { groupName: group.name }),
    );
    if (removable) {
      removeExtraGroupFromProduct({
        variables: {
          product_id: productId,
          group_id: group.extra_group_id,
        },
        // needed for updating variants
        refetchQueries: [
          {
            query: productCategoriesQuery,
            variables: {
              id: storeId,
              filter: 'ALL',
            },
          },
        ],
      });
    }
  }

  async function createAndAddNewExtra(e, group) {
    e.stopPropagation();
    if (!brandId) {
      return;
    }
    setIsAddingPlaceholderExtra(true);
    const ingestExtraRes = await ingestPlaceholderExtra({
      variables: {
        brand_id: brandId,
      },
    });

    const newExtraId = ingestExtraRes.data.ingestProduct.id;
    if (!newExtraId) {
      alert(i18n.t('Menu:insertNewExtraError'));
      return;
    }
    await addExtraToGroup({
      variables: {
        product_id: productId,
        group_id: group.extra_group_id,
        extra_id: newExtraId,
      },
      refetchQueries: [
        {
          query: productCategoriesQuery,
          variables: {
            id: storeId,
            filter: 'ALL',
          },
        },
        {
          query: brandCategoryProductsByStoreQuery,
          variables: {
            store_id: storeId,
          },
        },
      ],
      onError: (err) => {
        // eslint-disable-next-line no-console
        console.log(err);
        // eslint-disable-next-line no-alert
        alert(err);
      },
    });
    setIsAddingPlaceholderExtra(false);
  }

  const selectedExtraGroupIds = Object.keys(selectedExtraGroups).filter(
    (key) => selectedExtraGroups[key] === true,
  );

  const onCopyClick = () => {
    const extraGroupsToCopy = extraGroups.filter(
      (eg) => selectedExtraGroups[eg.extra_group_id] === true,
    );
    copyPasteContext.setExtraGroups([...extraGroupsToCopy]);
    setHasCopied(true);
    setSelectedExtraGroups({});
  };

  const onPasteClick = async () => {
    for (let i = 0; i < copyPasteContext.extraGroups.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await addExtraGroup({
        variables: {
          product_id: productId,
          group_id: copyPasteContext.extraGroups[i].extra_group_id,
        },
        refetchQueries: [
          {
            query: productCategoriesQuery,
            variables: {
              id: storeId,
              filter: 'ALL',
            },
          },
        ],
        onError: (err) => {
          // eslint-disable-next-line no-console
          console.log(err);
          // eslint-disable-next-line no-alert
          alert(err);
        },
      });
    }
  };

  return (
    <div>
      <CopyPasteButtonContainer>
        <Button
          disabled={selectedExtraGroupIds.length === 0}
          onClick={onCopyClick}
        >
          {!hasCopied && <Icon name="clipboard" paddingTop={3} />}
          {hasCopied
            ? ` ${i18n.t('Menu:copied')}!`
            : ` ${i18n.t('Menu:copy')} (${selectedExtraGroupIds.length})`}
        </Button>
        <Spacer />
        <Button
          disabled={copyPasteContext.extraGroups.length === 0}
          onClick={onPasteClick}
          loading={addingExtraGroup}
        >
          Paste
        </Button>
      </CopyPasteButtonContainer>
      {(extraGroups || []).map((group) => {
        const minReached = hasReachedMin(group);
        const maxReached = hasReachedMax(group);
        let extraHighlightText = '';
        if (group.minimum_extras && !minReached) {
          extraHighlightText = i18n.t('Menu:selectMinExtras', {
            minAmount: group.minimum_extras,
          });
        } else if (group.maximum_extras && maxReached) {
          extraHighlightText = i18n.t('Menu:selectMaxExtras', {
            maxAmount: group.maximum_extras,
          });
        }

        const extraGroupId = group.extra_group_id;

        const onExtraGroupSelect = () => {
          setHasCopied(false);
          setSelectedExtraGroups({
            ...selectedExtraGroups,
            [group.extra_group_id]: !selectedExtraGroups[group.extra_group_id],
          });
        };
        return (
          <ExtraSelectorContainer key={group.extra_group_id}>
            <TitleContainer>
              <TitleOption>
                <Checkbox
                  checked={!!selectedExtraGroups[group.extra_group_id]}
                  onClick={onExtraGroupSelect}
                />
                <ExtrasTitle
                  onClick={() => {
                    const groupId = group.extra_group_id;
                    setOpenGroup({
                      ...openGroup,
                      [groupId]: !openGroup[groupId],
                    });
                  }}
                  title={group.extra_group_id}
                >
                  {group.name}
                </ExtrasTitle>
                <ExtrasHighlight>{extraHighlightText}</ExtrasHighlight>
                <IconButton
                  onClick={(e) => openEditModal(e, group)}
                  name="edit"
                />
                <IconButton
                  onClick={(e) => openAddExtraModal(e, group)}
                  title={i18n.t('Menu:addExtra')}
                  name="plus"
                />
                <IconButton
                  onClick={(e) => removeExtraGroup(e, group)}
                  name="trash"
                  color="red"
                />
                <CreateExtraBtn
                  onClick={(e) => createAndAddNewExtra(e, group)}
                  small
                  loading={isAddingPlaceholderExtra}
                >
                  {i18n.t('Menu:createNewExtra')}
                </CreateExtraBtn>
              </TitleOption>
              <StyledIcon>
                <Icon
                  name={
                    openGroup[group.extra_group_id] ? 'angle_up' : 'angle_down'
                  }
                />
              </StyledIcon>
            </TitleContainer>
            {openGroup[group.extra_group_id] && (
              <ListContainer>
                {group.items.map((item) => {
                  const selected = selectedExtras.some(
                    (ex) => ex.extra_id === item.extra_id,
                  );
                  const disabled = maxReached && !selected;
                  return (
                    <ExtraSelectItem
                      key={item.extra_id}
                      extra={item}
                      group={group}
                      disabled={disabled}
                      selected={selected}
                      onChange={onExtraChange}
                      productId={productId}
                      storeId={storeId}
                    />
                  );
                })}
              </ListContainer>
            )}
            {editingGroup &&
              editingGroup.extra_group_id === group.extra_group_id && (
                <ExtraGroupEditModal
                  productId={productId}
                  extraGroup={editingGroup}
                  storeId={storeId}
                  onClose={closeEditModal}
                />
              )}
            {addExtraModalOpenFor === group.extra_group_id && (
              <AddExtraModal
                allExtrasForBrand={allExtrasForBrand}
                productId={productId}
                groupId={extraGroupId}
                storeId={storeId}
                onClose={closeAddExtraModal}
              />
            )}
          </ExtraSelectorContainer>
        );
      })}
    </div>
  );
}

export default ExtraSelector;
